/*
 Date typed in a single numeric field, masked in the order and with the separator of the page locale
 (31/12/1990, 31.12.1990, 1990/12/31…). The typed field has no name: the hidden `value` target
 carries the ISO date the server reads, and stays empty until the date is complete and real.

 <field-form data-controller="form--input--date-mask" data-form--input--date-mask-placeholders-value='{"day":"JJ","month":"MM","year":"AAAA"}'>
   <input type="text" inputmode="numeric" data-form--input--date-mask-target="input" data-action="form--input--date-mask#format">
   <input type="hidden" name="user[f_dob]" data-form--input--date-mask-target="value">
 </field-form>
 */

import { Controller } from '@hotwired/stimulus'

const LENGTHS = { day: 2, month: 2, year: 4 }

export default class extends Controller {
  static targets = ['input', 'value']
  static values = { placeholders: Object }

  connect() {
    const parts = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
      .formatToParts(new Date(2000, 0, 1))

    this.order = parts.filter(part => part.type in LENGTHS).map(part => part.type)
    this.separator = parts.find(part => part.type === 'literal')?.value.trim() || '/'

    this.inputTarget.placeholder = this.order.map(type => this.placeholdersValue[type]).join(this.separator)
    this.inputTarget.maxLength = 8 + 2 * this.separator.length

    if (this.valueTarget.value) {
      const [year, month, day] = this.valueTarget.value.split('-')
      const date = { year, month, day }
      this.inputTarget.value = this.order.map(type => date[type]).join(this.separator)
    }
  }

  format(event) {
    const input = this.inputTarget
    const caretAtEnd = input.selectionStart === input.value.length
    const digitsBeforeCaret = input.value.slice(0, input.selectionStart).replace(/\D/g, '').length
    let digits = input.value.replace(/\D/g, '').slice(0, 8)

    const chunks = []
    for (const type of this.order) {
      if (!digits) break
      chunks.push(digits.slice(0, LENGTHS[type]))
      digits = digits.slice(LENGTHS[type])
    }

    input.value = chunks.join(this.separator)

    // Separator shown as soon as a part is complete, but not put back while erasing it
    const lastType = this.order[chunks.length - 1]
    const partComplete = chunks.length < this.order.length && chunks.at(-1)?.length === LENGTHS[lastType]
    if (partComplete && caretAtEnd && !event?.inputType?.startsWith('delete')) input.value += this.separator

    this.valueTarget.value = this.#iso(chunks)
    this.#placeCaret(caretAtEnd ? null : digitsBeforeCaret)
  }

  // Empty unless every part is typed and the date exists (no 31/02)
  #iso(chunks) {
    if (chunks.join('').length < 8) return ''

    const date = Object.fromEntries(this.order.map((type, index) => [type, chunks[index]]))
    const parsed = new Date(Date.UTC(date.year, date.month - 1, date.day))
    if (parsed.getUTCFullYear() !== Number(date.year) || parsed.getUTCMonth() !== date.month - 1 || parsed.getUTCDate() !== Number(date.day)) return ''

    return `${date.year}-${date.month}-${date.day}`
  }

  // Keep the caret after the same digit once separators are added or removed, or at the end (null)
  #placeCaret(digits) {
    const input = this.inputTarget
    if (document.activeElement !== input) return

    if (digits === null) {
      input.setSelectionRange(input.value.length, input.value.length)
      return
    }

    let position = 0
    while (digits > 0 && position < input.value.length) {
      if (/\d/.test(input.value[position])) digits--
      position++
    }
    input.setSelectionRange(position, position)
  }
}
